import React, { useState, useEffect, useRef } from 'react';
import { playPhaseStart, playWarning, playTimeUp, playClick, playSpyReveal, playVote, playVictory, playCorrect } from '../sounds';

const PHASE_ORDER = ['roles', 'answering', 'discussion', 'voting', 'reveal', 'results'];

function Timer({ seconds, warning }) {
  return (
    <div className="timer-container">
      <div className={`timer-ring ${warning ? 'warning' : ''}`}>
        {seconds}
      </div>
    </div>
  );
}

function ProgressSteps({ currentPhase }) {
  const steps = ['roles', 'answering', 'discussion', 'voting', 'results'];
  const currentIdx = steps.indexOf(currentPhase === 'reveal' ? 'voting' : currentPhase);
  return (
    <div className="progress-steps">
      {steps.map((p, i) => {
        let cls = 'progress-step';
        if (i < currentIdx) cls += ' completed';
        else if (i === currentIdx) cls += ' active';
        return <div key={p} className={cls} />;
      })}
    </div>
  );
}

function Game({ gameState, playerId, roomCode, sendMessage, error, clearError }) {
  const {
    phase,
    currentRound,
    totalRounds,
    category,
    isSpy,
    question,
    options,
    chatMessages,
    discussionQuestion,
    discusserName,
    hasAnswered,
    hasVoted,
    answeredCount,
    votedCount,
    totalPlayers,
    players,
    phaseTimeLeft,
    suspectCounts,
    mySuspect,
    emojis,
    playerAnswers,
    spyId,
    spyName,
    spyCaught,
    correctAnswer,
    answers,
    votes,
    voteCounts,
    gameOver,
    roundTitle,
    revealedVotes,
  } = gameState;

  const [chatInput, setChatInput] = useState('');
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [selectedVote, setSelectedVote] = useState(null);
  const [localTimer, setLocalTimer] = useState(phaseTimeLeft);
  const [showReactionPicker, setShowReactionPicker] = useState(null);
  const chatEndRef = useRef(null);
  const isHost = players.find((p) => p.id === playerId)?.isHost;

  useEffect(() => {
    setLocalTimer(phaseTimeLeft);
  }, [phaseTimeLeft, phase]);

  useEffect(() => {
    if (phase === 'results' || phase === 'reveal') return;
    const interval = setInterval(() => {
      setLocalTimer((prev) => {
        const next = Math.max(0, prev - 1);
        if (next === 5 || next === 3) playWarning();
        if (next === 0 && prev > 0) playTimeUp();
        return next;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [phase]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  useEffect(() => {
    if (phase === 'answering') setSelectedAnswer(null);
    if (phase === 'voting') setSelectedVote(null);
    setShowReactionPicker(null);
    // Sound on phase change
    if (phase === 'results') {
      if (gameOver) playVictory();
      else playSpyReveal();
    } else if (phase === 'reveal') {
      playSpyReveal();
    } else {
      playPhaseStart();
    }
  }, [phase]);

  const sendChat = () => {
    if (!chatInput.trim()) return;
    sendMessage('CHAT_MESSAGE', { text: chatInput });
    setChatInput('');
  };

  const reactToMsg = (index, emoji) => {
    sendMessage('REACT_MESSAGE', { messageIndex: index, emoji });
    setShowReactionPicker(null);
  };

  const markSuspect = (suspectId) => {
    sendMessage('MARK_SUSPECT', { suspectId });
  };

  const submitAnswer = (index) => {
    setSelectedAnswer(index);
    playClick();
    sendMessage('SELECT_ANSWER', { answerIndex: index });
  };

  const submitVote = (suspectId) => {
    setSelectedVote(suspectId);
    playVote();
    sendMessage('VOTE_SPY', { suspectId });
  };

  const StatusBar = () => (
    <>
      <div className="status-bar">
        <span>סיבוב {currentRound} מתוך {totalRounds}</span>
        <span className="category-badge">{category}</span>
      </div>
      <ProgressSteps currentPhase={phase} />
    </>
  );

  // Group reactions by emoji for display
  const getReactionSummary = (reactions) => {
    const summary = {};
    for (const [, emoji] of Object.entries(reactions || {})) {
      summary[emoji] = (summary[emoji] || 0) + 1;
    }
    return summary;
  };

  // ===== ROLES PHASE =====
  if (phase === 'roles') {
    return (
      <div className="card">
        <StatusBar />
        <div className="phase-header">
          <span className="phase-step">שלב 1 - הכנה</span>
        </div>

        {isSpy ? (
          <div className="spy-reveal">
            <span className="spy-emoji" role="img" aria-label="spy">🕵️</span>
            <h2>אתה המרגל!</h2>
            <p style={{ marginTop: '8px', fontSize: '0.9rem', opacity: 0.9 }}>
              כולם קיבלו שאלת טריוויה מהקטגוריה "{category}" - חוץ ממך!
            </p>
            <p style={{ marginTop: '6px', fontSize: '0.85rem', color: '#ffcdd2' }}>
              אבל אתה כן רואה את התשובות האפשריות - השתמש בזה כדי לבלף!
            </p>
            {options && (
              <div style={{ marginTop: '12px', background: 'rgba(0,0,0,0.2)', borderRadius: '10px', padding: '10px' }}>
                <p style={{ fontSize: '0.75rem', marginBottom: '6px', opacity: 0.7 }}>התשובות האפשריות:</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'center' }}>
                  {options.map((opt, i) => (
                    <span key={i} style={{ background: 'rgba(255,255,255,0.1)', padding: '4px 10px', borderRadius: '8px', fontSize: '0.85rem' }}>
                      {opt}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="info-box warning">
              <p style={{ fontSize: '0.9rem', color: '#FF5252' }}>
                אחד מכם הוא מרגל! הוא רואה את התשובות אבל לא את השאלה
              </p>
            </div>
            <div className="info-box normal">
              <p className="info-box-label" style={{ color: '#b8a9d4' }}>השאלה שלכם</p>
              <p className="info-box-text">{question}</p>
            </div>
            <div className="options-grid">
              {options && options.map((opt, i) => (
                <div key={i} className="option-btn" style={{ cursor: 'default' }}>
                  {opt}
                </div>
              ))}
            </div>
          </>
        )}

        <Timer seconds={localTimer} warning={localTimer <= 2} />
        <p style={{ textAlign: 'center', color: '#9b8cbf', fontSize: '0.85rem' }}>
          קראו את השאלה - שלב הדיון מתחיל עוד רגע
        </p>
      </div>
    );
  }

  // ===== DISCUSSION PHASE =====
  if (phase === 'discussion') {
    return (
      <div className="card">
        <StatusBar />
        <div className="phase-header">
          <span className="phase-step">שלב 3 - דיון</span>
          <div className="phase-title">מי חשוד?</div>
          <div className="phase-subtitle">
            רואים למעלה מי ענה מה - דברו ביניכם! מי נראה כאילו ניחש? סמנו חשודים!
          </div>
        </div>

        <Timer seconds={localTimer} warning={localTimer <= 10} />

        {/* Show what everyone answered */}
        {options && playerAnswers && (
          <div style={{ marginBottom: '14px' }}>
            <p className="info-box-label" style={{ color: '#00D4AA', textAlign: 'center', marginBottom: '8px' }}>
              מה כולם ענו:
            </p>
            <div className="options-grid" style={{ gridTemplateColumns: '1fr' }}>
              {options.map((opt, i) => {
                const whoChose = Object.entries(playerAnswers)
                  .filter(([, a]) => a.answerIndex === i)
                  .map(([, a]) => a.name);
                return (
                  <div key={i} className="option-btn" style={{ cursor: 'default', textAlign: 'right', padding: '10px 14px' }}>
                    <span>{opt}</span>
                    {whoChose.length > 0 && (
                      <span className="option-label" style={{ color: '#00D4AA' }}>
                        {whoChose.join(', ')}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {!isSpy && question && (
          <div className="info-box normal" style={{ padding: '10px' }}>
            <p style={{ fontSize: '0.85rem', color: '#b8a9d4' }}>השאלה: <strong style={{ color: 'white' }}>{question}</strong></p>
          </div>
        )}

        {isSpy && (
          <div className="info-box spy" style={{ padding: '10px' }}>
            <p style={{ fontSize: '0.85rem', color: '#FF5252' }}>🕵️ אתה המרגל - נסה להסתלק!</p>
          </div>
        )}

        {/* Suspect marking */}
        <div style={{ marginBottom: '12px' }}>
          <p style={{ fontSize: '0.8rem', color: '#9b8cbf', marginBottom: '6px' }}>
            חשוד? לחצו לסמן:
          </p>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {players.filter((p) => p.id !== playerId).map((p) => {
              const isSuspected = mySuspect === p.id;
              const count = suspectCounts?.[p.id] || 0;
              return (
                <button
                  key={p.id}
                  onClick={() => markSuspect(p.id)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '20px',
                    border: isSuspected ? '2px solid #FF5252' : '2px solid rgba(255,255,255,0.1)',
                    background: isSuspected ? 'rgba(255,82,82,0.2)' : 'rgba(255,255,255,0.04)',
                    color: 'white',
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    fontFamily: 'Heebo, sans-serif',
                    transition: 'all 0.2s',
                  }}
                >
                  {p.name} {count > 0 && <span style={{ color: '#FF5252' }}>({count}🔍)</span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Chat */}
        <div className="chat-container">
          {chatMessages.length === 0 && (
            <p className="chat-empty">אין הודעות עדיין - היו הראשונים לכתוב!</p>
          )}
          {chatMessages.map((msg, i) => {
            const reactionSummary = getReactionSummary(msg.reactions);
            return (
              <div key={i} className="chat-message">
                <span className="sender">{msg.playerName}</span>
                <span className="text">{msg.text}</span>
                {/* Reactions display */}
                <div style={{ display: 'flex', gap: '4px', marginTop: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
                  {Object.entries(reactionSummary).map(([emoji, count]) => (
                    <span
                      key={emoji}
                      onClick={() => reactToMsg(i, emoji)}
                      style={{
                        background: 'rgba(255,255,255,0.08)',
                        padding: '1px 6px',
                        borderRadius: '10px',
                        fontSize: '0.75rem',
                        cursor: 'pointer',
                      }}
                    >
                      {emoji} {count > 1 && count}
                    </span>
                  ))}
                  <span
                    onClick={() => setShowReactionPicker(showReactionPicker === i ? null : i)}
                    style={{
                      cursor: 'pointer',
                      fontSize: '0.7rem',
                      color: '#9b8cbf',
                      padding: '1px 4px',
                    }}
                  >
                    +
                  </span>
                </div>
                {showReactionPicker === i && (
                  <div style={{
                    display: 'flex', gap: '6px', marginTop: '4px',
                    background: 'rgba(0,0,0,0.3)', padding: '6px 8px', borderRadius: '10px',
                  }}>
                    {(emojis || ['👍', '😂', '🤔', '😮', '🤥', '👀']).map((emoji) => (
                      <span
                        key={emoji}
                        onClick={() => reactToMsg(i, emoji)}
                        style={{ cursor: 'pointer', fontSize: '1.1rem' }}
                      >
                        {emoji}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          <div ref={chatEndRef} />
        </div>

        <div className="chat-input-row">
          <input
            className="input"
            placeholder="כתוב תגובה..."
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && sendChat()}
            maxLength={200}
          />
          <button className="btn btn-primary" onClick={sendChat} disabled={!chatInput.trim()}>
            שלח
          </button>
        </div>
      </div>
    );
  }

  // ===== ANSWERING PHASE =====
  if (phase === 'answering') {
    return (
      <div className="card">
        <StatusBar />
        <div className="phase-header">
          <span className="phase-step">שלב 2 - תשובה</span>
          <div className="phase-title">בחרו תשובה!</div>
          <div className="phase-subtitle">
            {isSpy
              ? 'נחש נכון בלי לדעת את השאלה - בונוס ענק!'
              : 'ענו מהר - יש בונוס מהירות! אחרי זה נראה מי ענה מה'}
          </div>
        </div>

        <Timer seconds={localTimer} warning={localTimer <= 8} />

        {!isSpy && question && (
          <div className="info-box normal">
            <p className="info-box-text">{question}</p>
          </div>
        )}

        {isSpy && (
          <div className="info-box spy">
            <p style={{ fontSize: '0.85rem', color: '#FF5252' }}>
              🕵️ אתה לא רואה את השאלה - נחש מהתשובות!
            </p>
          </div>
        )}

        <div className="options-grid">
          {(options || ['?', '?', '?', '?']).map((opt, i) => (
            <button
              key={i}
              className={`option-btn ${selectedAnswer === i ? 'selected' : ''}`}
              onClick={() => !hasAnswered && submitAnswer(i)}
              disabled={hasAnswered}
            >
              {opt}
            </button>
          ))}
        </div>

        {hasAnswered && (
          <p className="waiting">
            בחרת! ממתינים לשאר ({answeredCount}/{totalPlayers})
          </p>
        )}
      </div>
    );
  }

  // ===== VOTING PHASE =====
  if (phase === 'voting') {
    return (
      <div className="card">
        <StatusBar />
        <div className="phase-header">
          <span className="phase-step">שלב 4 - הצבעה</span>
          <div className="phase-title">מי המרגל?</div>
          <div className="phase-subtitle">
            לחצו על מי שלדעתכם בלף. תפיסה נכונה = 5 נקודות!
          </div>
        </div>

        <Timer seconds={localTimer} warning={localTimer <= 5} />

        {/* Show suspect counts from discussion */}
        {suspectCounts && Object.keys(suspectCounts).length > 0 && (
          <div className="info-box" style={{ background: 'rgba(255,82,82,0.06)', border: '1px solid rgba(255,82,82,0.1)', marginBottom: '12px' }}>
            <p style={{ fontSize: '0.8rem', color: '#9b8cbf', marginBottom: '4px' }}>סומנו כחשודים בדיון:</p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
              {Object.entries(suspectCounts).map(([pid, count]) => {
                const name = players.find((p) => p.id === pid)?.name;
                return (
                  <span key={pid} style={{ color: '#FF5252', fontSize: '0.9rem' }}>
                    {name} ({count}🔍)
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {players
          .filter((p) => p.id !== playerId)
          .map((p) => {
            const susp = suspectCounts?.[p.id] || 0;
            return (
              <button
                key={p.id}
                className={`vote-btn ${selectedVote === p.id ? 'selected' : ''}`}
                onClick={() => !hasVoted && submitVote(p.id)}
                disabled={hasVoted}
              >
                <span>{p.name}</span>
                {susp > 0 && <span style={{ fontSize: '0.8rem', color: '#FF5252' }}>{susp}🔍</span>}
              </button>
            );
          })}

        {hasVoted && (
          <p className="waiting">
            הצבעת! ממתינים לשאר ({votedCount}/{totalPlayers})
          </p>
        )}
      </div>
    );
  }

  // ===== REVEAL PHASE - Dramatic vote reveal =====
  if (phase === 'reveal') {
    const revealed = revealedVotes || {};
    const revealedList = Object.values(revealed);

    return (
      <div className="card">
        <StatusBar />
        <div className="phase-header">
          <div className="phase-title" style={{ fontSize: '1.6rem' }}>חושפים את ההצבעות...</div>
          <div className="phase-subtitle">מי הצביע למי?</div>
        </div>

        <div style={{ minHeight: '150px' }}>
          {revealedList.map((v, i) => (
            <div
              key={i}
              className="player-item"
              style={{
                animation: 'slideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
                marginBottom: '8px',
                background: 'rgba(255,82,82,0.08)',
                border: '1px solid rgba(255,82,82,0.15)',
              }}
            >
              <span>{v.voterName}</span>
              <span style={{ color: '#FF5252', fontWeight: 700 }}>
                הצביע/ה: {v.suspectName}
              </span>
            </div>
          ))}

          {revealedList.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div style={{
                width: '40px', height: '40px', border: '3px solid rgba(255,82,82,0.3)',
                borderTop: '3px solid #FF5252', borderRadius: '50%',
                animation: 'spin 1s linear infinite', margin: '0 auto 12px',
              }} />
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              <p style={{ color: '#9b8cbf' }}>מתכוננים לחשיפה...</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ===== RESULTS PHASE =====
  if (phase === 'results') {
    const sortedPlayers = [...players].sort((a, b) => b.score - a.score);

    return (
      <div className="card">
        <StatusBar />

        {gameOver ? (
          <div className="game-over-title">המשחק נגמר!</div>
        ) : (
          <>
            {/* Round title/achievement */}
            {roundTitle && (
              <div style={{
                textAlign: 'center', marginBottom: '16px',
                animation: 'bounceIn 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
              }}>
                <span style={{ fontSize: '2.5rem', display: 'block' }}>{roundTitle.icon}</span>
                <h2 style={{ color: '#FFD700', fontSize: '1.4rem' }}>{roundTitle.title}</h2>
                <p style={{ color: '#9b8cbf', fontSize: '0.9rem' }}>{roundTitle.desc}</p>
              </div>
            )}
          </>
        )}

        {/* Spy reveal */}
        <div className={`result-spy ${spyCaught ? 'caught' : 'escaped'}`}>
          <span style={{ fontSize: '2.5rem', display: 'block', marginBottom: '6px' }}>🕵️</span>
          <h3 style={{ fontSize: '1.2rem' }}>המרגל היה: {spyName}</h3>
          <p style={{ marginTop: '6px', fontSize: '0.95rem' }}>
            {spyCaught ? 'נתפס! כל הכבוד' : 'לא נתפס! בלוף מוצלח'}
          </p>
        </div>

        {/* Correct answer */}
        <div className="result-section">
          <h3>התשובה הנכונה</h3>
          {question && <p style={{ marginBottom: '10px', color: '#b8a9d4', fontSize: '0.9rem' }}>{question}</p>}
          <div className="options-grid" style={{ gridTemplateColumns: '1fr' }}>
            {options && options.map((opt, i) => {
              const isCorrect = i === correctAnswer;
              const playersWhoChose = answers
                ? Object.entries(answers).filter(([, a]) => a.answerIndex === i).map(([, a]) => a.name)
                : [];
              return (
                <div
                  key={i}
                  className={`option-btn ${isCorrect ? 'correct' : playersWhoChose.length > 0 ? 'wrong' : ''}`}
                  style={{ cursor: 'default', textAlign: 'right' }}
                >
                  <span>{opt} {isCorrect && ' ✓'}</span>
                  {playersWhoChose.length > 0 && (
                    <span className="option-label">{playersWhoChose.join(', ')}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Votes */}
        <div className="result-section">
          <h3>הצבעות</h3>
          {players.map((p) => {
            const voteCount = voteCounts && voteCounts[p.id] ? voteCounts[p.id] : 0;
            const wasSpy = p.id === spyId;
            return (
              <div key={p.id} className="player-item" style={{
                borderRight: wasSpy ? '3px solid #FF5252' : 'none',
              }}>
                <span className="player-name">
                  {p.name} {wasSpy && '🕵️'}
                </span>
                <span style={{ color: voteCount > 0 ? '#FF5252' : '#9b8cbf', fontWeight: 700 }}>
                  {voteCount > 0 ? `${voteCount} הצבעות` : '-'}
                </span>
              </div>
            );
          })}
        </div>

        {/* Scoring */}
        <div className="scoring-legend">
          <strong>ניקוד:</strong> תשובה נכונה = 10+בונוס מהירות | תפיסת מרגל = 5 | מרגל שלא נתפס = 15 | מרגל שגם ניחש נכון = 25
        </div>

        {/* Leaderboard */}
        <div className="result-section">
          <h3>טבלת ניקוד</h3>
          <div className="leaderboard">
            {sortedPlayers.map((p, i) => (
              <div key={p.id} className="leaderboard-item">
                <span className="leaderboard-rank">
                  {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}.`}
                </span>
                <span className="leaderboard-name">
                  {p.name}
                  {p.id === playerId && <span className="me-badge"> (אני)</span>}
                </span>
                <span className="leaderboard-score">{p.score}</span>
              </div>
            ))}
          </div>
        </div>

        {error && <p className="error-msg">{error}</p>}

        {isHost && !gameOver && (
          <button className="btn btn-primary" onClick={() => { clearError(); sendMessage('NEXT_ROUND', {}); }}>
            סיבוב הבא
          </button>
        )}

        {isHost && gameOver && (
          <>
            <button className="btn btn-primary" onClick={() => { clearError(); sendMessage('NEW_GAME', {}); }}>
              משחק חדש
            </button>
            <button
              className="btn btn-ghost"
              onClick={() => {
                const text = `🕵️ SpyQ - תוצאות!\n\n${sortedPlayers.map((p, i) => `${i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i+1}.`} ${p.name}: ${p.score} נקודות`).join('\n')}\n\nשחקו גם: ${window.location.origin}`;
                navigator.clipboard.writeText(text).catch(() => {});
              }}
            >
              שתף תוצאות
            </button>
          </>
        )}

        {!isHost && !gameOver && (
          <p className="waiting">ממתינים שהמנהל ימשיך לסיבוב הבא...</p>
        )}
      </div>
    );
  }

  return (
    <div className="card">
      <p style={{ textAlign: 'center', color: '#9b8cbf' }}>טוען...</p>
    </div>
  );
}

export default Game;
